import { useState, useRef, useMemo, useCallback, memo } from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { QuoteSummary, ProcessesedRows } from "../types/models";
import { Stack, styled } from "~/common/mui";
import { Alert } from "~/common/mui/Alert/Alert";
import CheckMessagesComponent from "~/common/components/CheckMessagesComponent";
import { QuotePreviewItem } from "./QuotePreviewItem";
import { useQuoteComputedData } from "./useQuoteComputedData";
import { LinkButton } from "~/common/components/Link";

interface QuoteSplitUploadPreviewProps {
  actualQuoteDetails: QuoteSummary[];
  entryErrorRows: string[];
  dateModifiedRows: string[];
  quantityErrorRows: string[];
}

interface PreviewFormQuoteRow {
  quoteNumber: string;
  items: ProcessesedRows[];
}

export interface PreviewTableRef {
  scrollToRow: (index: number) => void;
}

export const QuoteSplitUploadPreview = memo(
  ({ actualQuoteDetails, entryErrorRows, dateModifiedRows, quantityErrorRows }: QuoteSplitUploadPreviewProps) => {
    const { t } = useTranslation();
    const [showEntryErrorAlert, setShowEntryErrorAlert] = useState(!!entryErrorRows.length);

    const filteredQuoteList = useWatch({ name: "rows" });
    const quoteComputedData = useQuoteComputedData(filteredQuoteList, actualQuoteDetails);

    const parentRef = useRef<HTMLDivElement>(null);
    const rowVirtualizer = useVirtualizer({
      count: filteredQuoteList.length,
      getScrollElement: () => parentRef.current?.closest(".MuiDialogContent-root") ?? null,
      estimateSize: () => 250,
      overscan: 5,
    });

    const quoteIndexMap = useMemo(() => {
      const map = new Map<string, number>();
      filteredQuoteList.forEach((q: PreviewFormQuoteRow, index: number) => {
        map.set(q.quoteNumber, index);
      });
      return map;
    }, [filteredQuoteList]);

    const scrollToQuotePreviewCard = useCallback(
      (quoteNumber: string) => {
        const index = quoteIndexMap.get(quoteNumber);
        if (index !== undefined) {
          rowVirtualizer.scrollToIndex(index, {
            align: "center",
            behavior: "smooth",
          });
        }
      },
      [quoteIndexMap, rowVirtualizer],
    );

    const scrollToErrorMsg = (index: number) => {
      scrollToQuotePreviewCard(quantityErrorRows?.[index]);
    };

    return (
      <Stack direction="column" gap={1.5} ref={parentRef}>
        {showEntryErrorAlert && (
          <AlertContainer
            severity={"error"}
            action={
              <DismissButton onClick={() => setShowEntryErrorAlert(false)}>{t("homepage.cart.dismiss")}</DismissButton>
            }
          >
            {t("quotation.split.preview.errorMessage.quoteNumber", {
              count: entryErrorRows.length,
              quoteNumbers: entryErrorRows.join(", "),
            })}
          </AlertContainer>
        )}

        <CheckMessagesComponent
          msgType={"error"}
          msgCount={quantityErrorRows.length}
          onScrollToMessage={scrollToErrorMsg}
          sticky={true}
          isBulkUploadPreview={true}
        />
        {dateModifiedRows.length > 0 && (
          <Alert severity={"info"}>{t("quotation.split.preview.errorMessage.rdd")}</Alert>
        )}
        <div
          style={{
            height: rowVirtualizer.getTotalSize(),
            width: "100%",
            position: "relative",
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const quote = filteredQuoteList[virtualRow.index];
            const computed = quoteComputedData.get(quote.quoteNumber);

            if (!computed) {
              return null;
            }

            const { confirmedQtyMap, positionOptions, materialNumberOptions, quoteDetails } = computed;

            return (
              <div
                key={quote.quoteNumber}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <QuotePreviewItem
                  key={quote.quoteNumber}
                  actualQuoteIndex={virtualRow.index}
                  actualQuoteDetails={quoteDetails as QuoteSummary}
                  confirmedQtyMap={confirmedQtyMap}
                  positionOptions={positionOptions}
                  materialNumberOptions={materialNumberOptions}
                />
              </div>
            );
          })}
        </div>
      </Stack>
    );
  },
);

const AlertContainer = styled(Alert)({
  width: "100%",
  maxWidth: "unset",

  [`.MuiAlert-message`]: {
    flex: 1,
    display: "flex",
    justifyContent: "space-between",
  },
});

const DismissButton = styled(LinkButton)(({ theme }) => ({
  color: theme.palette.onErrorContainer.main,
  "&:hover": {
    color: theme.palette.onErrorContainer.main,
  },
}));
